import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { MotifTable } from '@ey-xd/motif-react';
import { handleGridReady } from '../../common/AgGridEventHandlers';
import CustomPagination from '../../common/MotifPagination';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate';
import MasterDataManagementTRSCellRenderer from '../shared/customRenderers/MasterDataManagementTRSCellRenderer';
import DateComparator from '../shared/customComparators/DateComparator';
import * as formatters from '../../scripts/formFormatters';

const MasterDataManagementTRSTable = ({
  trsData,
  handleEditButtonClick,
  handleDeleteButtonClick,
  handleGridChanges,
  gridColumnState,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  const onCellKeyDown = useCallback(
    (event) => {
      if (event.event.key === 'Enter' || event.event.key == ' ') {
        if (event.colDef.field == 'editactions') {
          handleEditButtonClick(event.data);
        }
        if (!event.data.isCannotBeDeleted) {
          if (event.colDef.field == 'deleteactions') {
            handleDeleteButtonClick(event.data);
          }
        }
      }
    },
    [handleEditButtonClick, handleDeleteButtonClick]
  );

  const handleFilterChanged = useCallback(() => {
    const totalPages = gridRef.current.api.paginationGetTotalPages();
    setTotalPages(totalPages);
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage]);

  // Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 150,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  const dateOptions = useMemo(
    () => ({
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'reitSuiteEntityIDForREIT',
        headerName: 'REITSuite Entity ID for REIT',
        sortable: true,
        minWidth: 200,
        valueGetter: (params) => {
          return params.data?.reitSuiteEntityIDForREIT || '';
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('REITSuiteEntityIDforREIT'),
        },
      },
      {
        field: 'reitShortName',
        headerName: 'REIT Name',
        sortable: true,
        minWidth: 150,
        headerComponentParams: { template: CustomHeaderTemplate('REITName') },
      },
      {
        field: 'isActive',
        headerName: 'REIT Status',
        sortable: true,
        minWidth: 130,
        valueGetter: (params) => {
          return params.data?.isActive === true ? 'Active' : 'Inactive';
        },
        headerComponentParams: { template: CustomHeaderTemplate('REITStatus') },
      },

      {
        field: 'trsName',
        headerName: 'TRS Name',
        sortable: true,
        minWidth: 150,
        headerComponentParams: { template: CustomHeaderTemplate('TRSName') },
      },
      {
        field: 'trsElectionDate',
        headerName: 'TRS Election Date',
        sortable: true,
        minWidth: 170,
        valueGetter: (params) => {
          const dateValue =
            params.data && params.data.trsElectionDate
              ? params.data.trsElectionDate
              : null;
          return dateValue ? formatters.formatDate(dateValue) : null;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('TRSElectionDate'),
        },
      },
      {
        field: 'trsInitialQuarter',
        headerName: 'Initial Quarter',
        sortable: true,
        minWidth: 150,
        headerComponentParams: {
          template: CustomHeaderTemplate('InitialQuarter'),
        },
      },
      {
        field: 'trsEndDate',
        headerName: 'End Date',
        sortable: true,
        minWidth: 150,
        valueGetter: (params) => {
          const dateValue =
            params.data && params.data.trsEndDate
              ? params.data.trsEndDate
              : null;
          return dateValue ? formatters.formatDate(dateValue) : null;
        },
        headerComponentParams: { template: CustomHeaderTemplate('TRSEndDate') },
      },
      {
        field: 'trsFinalQuarter',
        headerName: 'Final Quarter',
        sortable: true,
        minWidth: 150,
        headerComponentParams: {
          template: CustomHeaderTemplate('FinalQuarter'),
        },
      },

      {
        field: 'lastUpdatedBy',
        headerName: 'Last Updated By',
        sortable: true,
        minWidth: 180,
        hide: true,
        headerComponentParams: {
          template: CustomHeaderTemplate('LastUpdatedBy'),
        },
      },
      {
        field: 'lastUpdatedDate',
        headerName: 'Last Updated Date',
        sortable: true,
        minWidth: 180,
        hide: true,
        valueGetter: (params) => {
          const dateValue =
            params.data && params.data.lastUpdatedDateTime
              ? params.data.lastUpdatedDateTime
              : null;
          return dateValue
            ? new Date(dateValue + 'Z').toLocaleDateString('en-US', dateOptions)
            : '';
        },
        comparator: DateComparator,
        headerComponentParams: {
          template: CustomHeaderTemplate('LastUpdatedDate'),
        },
      },
      {
        field: 'editactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 80,
        maxWidth: 80,
        cellRenderer: (params) => {
          if (params.data?.taxableREITSubsidiaryMasterID === -1) {
            return null;
          }
          return <MasterDataManagementTRSCellRenderer {...params} />;
        },
        cellRendererParams: { handleEditButtonClick },
      },
      {
        field: 'deleteactions',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        suppressSeparator: true,
        sortable: false,
        minWidth: 80,
        maxWidth: 80,
        cellRenderer: (params) => {
          if (params.data?.taxableREITSubsidiaryMasterID === -1) {
            return null;
          }
          return <MasterDataManagementTRSCellRenderer {...params} />;
        },
        cellRendererParams: { handleDeleteButtonClick },
      },
    ],
    [handleEditButtonClick, handleDeleteButtonClick, dateOptions]
  );

  useEffect(() => {
    if (rowData.length > 0) {
      let calcTotalPages =
        gridRef &&
        gridRef.current &&
        gridRef.current.api &&
        gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      if (calcTotalPages === 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, [rowData]);

  useEffect(() => {
    setRowData(trsData);
    if (gridRef && gridRef.current && gridRef.current.api) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }
  }, [trsData]);

  useEffect(() => {
    handleGridChanges(
      rowData,
      gridRef && gridRef.current ? gridRef.current.api : null
    );
  }, [rowData]);

  useEffect(() => {
    let localStorageColumnState = localStorage.getItem(
      'REITSuiteTRSDataColumnState'
    );
    if (
      !localStorageColumnState &&
      gridRef &&
      gridRef.current &&
      gridRef.current.api
    ) {
      gridRef.current.api.setColumnDefs([]);
      gridRef.current.api.setColumnDefs(columnDefs);
    }
  }, [gridColumnState]);

  const handleFirstDataRenderedEvent = useCallback((event) => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.autoSizeAllColumns();
    }
  }, []);

  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    gridRef.current.api.paginationGoToPage(pageNumber - 1);
  }, []);

  const handleItemsPerPageChange = useCallback((pageSize) => {
    setItemsPerPage(pageSize);
    gridRef.current.api.paginationSetPageSize(Number(pageSize));
    setCurrentPage(1);
    gridRef.current.api.paginationGoToPage(0);
    setTotalPages(gridRef.current.api.paginationGetTotalPages());
  }, []);

  const instructionText =
    'Click on Actions > Click Export TRS Import Template > Update the downloaded file with the TRS details > Click Upload TRS Import Template.';
  const overlayNoRowsTemplate = `<div class="mb-3">${instructionText}</div>`;
  const hasData = trsData && trsData.length > 0;

  return (
    <React.Fragment>
      <div className="row mb-10">
        <div className="col mt-3">
          <div
            data-testid="grdTrsDataGrid"
            className={`ag-theme-quartz motif-table ${hasData ? 'table-bd-full-height-width' : 'empty-table-container'}`}
          >
            <MotifTable
              zebra={true}
              compact={true}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              accentedSort={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onGridReady={handleGridReady}
              onFirstDataRendered={handleFirstDataRenderedEvent}
              ref={gridRef}
              pagination={true}
              suppressPaginationPanel={true}
              paginationPageSize={100}
              groupDisplayType={'custom'}
              groupSelectsChildren={true}
              groupDefaultExpanded={0}
              onFilterChanged={handleFilterChanged}
              data-testid="grdTrsDataGrid"
              overlayNoRowsTemplate={overlayNoRowsTemplate}
              onCellKeyDown={onCellKeyDown}
            />

            {rowData.length > 0 && (
              <div className="mt-3">
                <span className="right mb-3">
                  <CustomPagination
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                    handleItemsPerPageChange={handleItemsPerPageChange}
                    gridRef={gridRef}
                    setCurrentPage={setCurrentPage}
                    setItemsPerPage={setItemsPerPage}
                    setTotalPages={setTotalPages}
                  />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

MasterDataManagementTRSTable.propTypes = {
  trsData: PropTypes.array.isRequired,
  handleEditButtonClick: PropTypes.func,
  handleDeleteButtonClick: PropTypes.func,
  handleViewButtonClick: PropTypes.func,
  handleGridChanges: PropTypes.func.isRequired,
  gridColumnState: PropTypes.object,
  handleGridColumnStateChange: PropTypes.func.isRequired,
  refreshGridTRSData: PropTypes.object,
};

export default MasterDataManagementTRSTable;
